import type { Ref } from 'vue'
import type { KeyboardShortcut } from '~/composables/ui/useUserPreferences'
import { displayKeysForShortcut, shortcutMatchesEvent } from '~/composables/ui/useUserShortcuts'

const SUBLIMATION_RESET_MS = 1200

type ModifierPressedRefs = {
  metaPressed: Ref<boolean>
  ctrlPressed: Ref<boolean>
  altPressed: Ref<boolean>
  shiftPressed: Ref<boolean>
  letterPressed: Ref<boolean>
}

function normalizeKey(key: string | undefined | null): string {
  return (key ?? '').trim().toLowerCase()
}

function buildPressedKeySegments(
  shortcut: KeyboardShortcut,
  modifiers: ModifierPressedRefs,
): boolean[] {
  const segmentPressState: boolean[] = []
  if (shortcut.meta) segmentPressState.push(modifiers.metaPressed.value)
  if (shortcut.ctrl) segmentPressState.push(modifiers.ctrlPressed.value)
  if (shortcut.alt) segmentPressState.push(modifiers.altPressed.value)
  if (shortcut.shift) segmentPressState.push(modifiers.shiftPressed.value)
  segmentPressState.push(modifiers.letterPressed.value)
  return segmentPressState
}

function syncModifiersFromEvent(
  shortcut: KeyboardShortcut,
  event: KeyboardEvent,
  modifiers: ModifierPressedRefs,
): void {
  if (shortcut.meta) modifiers.metaPressed.value = event.metaKey
  if (shortcut.ctrl) modifiers.ctrlPressed.value = event.ctrlKey
  if (shortcut.alt) modifiers.altPressed.value = event.altKey
  if (shortcut.shift) modifiers.shiftPressed.value = event.shiftKey
}

function releaseModifierOnKeyUp(
  shortcut: KeyboardShortcut,
  key: string | undefined,
  modifiers: ModifierPressedRefs,
): void {
  if (shortcut.meta && key === 'Meta') modifiers.metaPressed.value = false
  if (shortcut.ctrl && key === 'Control') modifiers.ctrlPressed.value = false
  if (shortcut.alt && key === 'Alt') modifiers.altPressed.value = false
  if (shortcut.shift && key === 'Shift') modifiers.shiftPressed.value = false
  if (normalizeKey(key) === normalizeKey(shortcut.key)) {
    modifiers.letterPressed.value = false
  }
}

function resetAllKeyState(modifiers: ModifierPressedRefs): void {
  modifiers.metaPressed.value = false
  modifiers.ctrlPressed.value = false
  modifiers.altPressed.value = false
  modifiers.shiftPressed.value = false
  modifiers.letterPressed.value = false
}

/**
 * État d’appui + sublimation pour animer `SourceKbd` selon un `KeyboardShortcut` utilisateur
 * (toolbox, Me situer, mode édition, etc.).
 */
export function useShortcutKeyVisualFeedback(shortcutRef: Ref<KeyboardShortcut>) {
  const modifiers: ModifierPressedRefs = {
    metaPressed: ref(false),
    ctrlPressed: ref(false),
    altPressed: ref(false),
    shiftPressed: ref(false),
    letterPressed: ref(false),
  }
  const sublimationTriggered = ref(false)

  const keys = computed(() => displayKeysForShortcut(shortcutRef.value))
  const pressedKeys = computed(() => buildPressedKeySegments(shortcutRef.value, modifiers))

  function onKeyDown(event: KeyboardEvent): void {
    const shortcut = shortcutRef.value
    syncModifiersFromEvent(shortcut, event, modifiers)
    if (normalizeKey(event.key) === normalizeKey(shortcut.key)) {
      modifiers.letterPressed.value = true
    }
    if (!shortcutMatchesEvent(shortcut, event)) return

    sublimationTriggered.value = true
    globalThis.setTimeout(() => {
      sublimationTriggered.value = false
      resetAllKeyState(modifiers)
    }, SUBLIMATION_RESET_MS)
  }

  function onKeyUp(event: KeyboardEvent): void {
    const shortcut = shortcutRef.value
    releaseModifierOnKeyUp(shortcut, event.key, modifiers)
    syncModifiersFromEvent(shortcut, event, modifiers)
  }

  return {
    keys,
    pressedKeys,
    sublimationTriggered,
    onKeyDown,
    onKeyUp,
  }
}
